import { useEffect, useState, type ReactNode } from "react";
import { Bookmark, Home, MessageCircle, Moon, Search, Settings, Store, Sun, UserCircle } from "lucide-react";
import { NavLink, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { getUser } from "../lib/data";
import { useAsync } from "../lib/useAsync";
import { displayUserName, UserAvatar } from "./UserAvatar";

type Theme = "light" | "dark";

function getInitialTheme(): Theme {
  const saved = localStorage.getItem("theme");
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function Shell({ children }: { children: ReactNode }) {
  const { user, logout, error } = useAuth();
  const { data: profile } = useAsync(() => (user ? getUser(user.uid) : Promise.resolve(null)), [user?.uid]);
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("theme", theme);
  }, [theme]);

  const isDark = theme === "dark";

  return (
    <div className="app-shell">
      <header className="topbar">
        <NavLink className="brand" to="/">
          My Local Forum
        </NavLink>
        <nav className="topnav" aria-label="Primary">
          <NavLink to="/store" title="Store">
            <Store size={21} />
          </NavLink>
          <NavLink to="/search" title="Search">
            <Search size={21} />
          </NavLink>
          <NavLink to="/settings" title="Settings">
            <Settings size={21} />
          </NavLink>
          <button
            className="icon-button"
            onClick={() => setTheme(isDark ? "light" : "dark")}
            title={isDark ? "Switch to light theme" : "Switch to dark theme"}
            aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
          >
            {isDark ? <Sun size={21} /> : <Moon size={21} />}
          </button>
          {user ? (
            <>
              <Link className="avatar-link" to="/profile" title="Profile" aria-label="Profile">
                <UserAvatar name={displayUserName(profile, user.email || "You")} imageUrl={profile?.profilePictureUrl || user.photoURL} size="sm" />
              </Link>
              <button className="ghost-button" onClick={logout}>
                Sign out
              </button>
            </>
          ) : (
            <Link className="ghost-button" to="/auth">
              Sign in
            </Link>
          )}
        </nav>
      </header>
      {error ? <div className="auth-error">{error}</div> : null}

      <main>{children}</main>

      <nav className="bottomnav" aria-label="Forum sections">
        <NavLink to="/">
          <Home size={20} />
          <span>Home</span>
        </NavLink>
        <NavLink to="/feed">
          <Bookmark size={20} />
          <span>Feeds</span>
        </NavLink>
        <NavLink to="/chat">
          <MessageCircle size={20} />
          <span>Chat</span>
        </NavLink>
        <NavLink to="/profile">
          {user ? (
            <UserAvatar name={displayUserName(profile, user.email || "You")} imageUrl={profile?.profilePictureUrl || user.photoURL} size="sm" />
          ) : (
            <UserCircle size={20} />
          )}
          <span>Profile</span>
        </NavLink>
      </nav>
    </div>
  );
}
